import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";

const AdminServices = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [expandedLogs, setExpandedLogs] = useState({});
  const [serviceLogs, setServiceLogs] = useState({});
  const [logsLoading, setLogsLoading] = useState({});

  useEffect(() => {
    const isAdmin = localStorage.getItem('shootzu_admin');
    if (!isAdmin) {
      navigate('/szadmin');
      return;
    }
    fetchServices();

    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchServices, 30000);
    return () => clearInterval(interval);
  }, [navigate]);

  const fetchServices = async () => {
    try {
      setRefreshing(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/services`
      );
      setServices(response.data.services || []);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to fetch services:', error);
      toast.error('Failed to load service status');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleStart = async (serviceName) => {
    try {
      setActionLoading(prev => ({ ...prev, [serviceName]: 'start' }));
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/services/${serviceName}/start`
      );
      toast.success(`${serviceName} started successfully`);
      await fetchServices();
    } catch (error) {
      console.error('Failed to start service:', error);
      toast.error(error.response?.data?.detail || 'Failed to start service');
    } finally {
      setActionLoading(prev => ({ ...prev, [serviceName]: null }));
    }
  };

  const handleStop = async (serviceName) => {
    try {
      setActionLoading(prev => ({ ...prev, [serviceName]: 'stop' }));
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/services/${serviceName}/stop`
      );
      toast.success(`${serviceName} stopped successfully`);
      await fetchServices();
    } catch (error) {
      console.error('Failed to stop service:', error);
      toast.error(error.response?.data?.detail || 'Failed to stop service');
    } finally {
      setActionLoading(prev => ({ ...prev, [serviceName]: null }));
    }
  };

  const handleRestart = async (serviceName) => {
    try {
      setActionLoading(prev => ({ ...prev, [serviceName]: 'restart' }));
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/services/${serviceName}/restart`
      );
      toast.success(`${serviceName} restarted successfully`);
      await fetchServices();
    } catch (error) {
      console.error('Failed to restart service:', error);
      toast.error(error.response?.data?.detail || 'Failed to restart service');
    } finally {
      setActionLoading(prev => ({ ...prev, [serviceName]: null }));
    }
  };

  const toggleLogs = async (serviceName) => {
    if (expandedLogs[serviceName]) {
      setExpandedLogs(prev => ({ ...prev, [serviceName]: false }));
      return;
    }

    setExpandedLogs(prev => ({ ...prev, [serviceName]: true }));
    await fetchLogs(serviceName);
  };

  const fetchLogs = async (serviceName) => {
    try {
      setLogsLoading(prev => ({ ...prev, [serviceName]: true }));
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/services/${serviceName}/logs?lines=50`
      );
      setServiceLogs(prev => ({ ...prev, [serviceName]: response.data.logs }));
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      toast.error('Failed to load logs');
    } finally {
      setLogsLoading(prev => ({ ...prev, [serviceName]: false }));
    }
  };

  const allOnline = services.every(s => s.status === 'online');
  const anyOffline = services.some(s => s.status !== 'online');

  // Status palette: online (green) vs everything else (red/stopped)
  const statusStyle = (status) => {
    if (status === 'online') {
      return { label: 'ONLINE', fg: '#15803D', bg: '#DCFCE7', dot: '#22C55E', ring: 'rgba(34,197,94,.18)' };
    }
    if (status === 'errored') {
      return { label: 'ERROR', fg: '#C2410C', fgBg: '#FFEDD5', bg: '#FFEDD5', dot: '#F97316', ring: 'rgba(249,115,22,.18)' };
    }
    return { label: 'STOPPED', fg: '#B91C1C', bg: '#FEE2E2', dot: '#EF4444', ring: 'rgba(239,68,68,.18)' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ padding: '80px 0' }}>
        <Loader2 className="animate-spin" style={{ width: 30, height: 30 }} color="#572B7C" />
      </div>
    );
  }

  return (
    <div className="min-h-full">
      {/* Sticky header */}
      <header className="sz-header flex items-center justify-between gap-[18px] flex-wrap">
        <div>
          <h1 className="sz-h1 m-0">Services</h1>
          <div className="text-[12.5px] text-[#8A90A2] mt-[3px]">
            Background workers &amp; system health
            {lastUpdated && <> · updated {lastUpdated.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</>}
          </div>
        </div>
        <button
          onClick={fetchServices}
          disabled={refreshing}
          className="sz-btn sz-btn-outline"
        >
          <RefreshCw className={`w-[15px] h-[15px] ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </header>

      {/* Scroll body */}
      <main className="px-[30px] pt-[22px] pb-[56px] max-w-[1080px]">
        {/* Overall status banner */}
        <div
          className="flex items-center gap-[11px] px-[18px] py-[14px] rounded-[14px] mb-[18px]"
          style={{
            background: anyOffline ? '#FEF2F2' : '#F0FDF4',
            border: `1px solid ${anyOffline ? '#FECACA' : '#BBF7D0'}`,
          }}
        >
          {anyOffline ? (
            <AlertTriangle className="w-5 h-5 flex-none" color="#B91C1C" strokeWidth={2.2} />
          ) : (
            <CheckCircle2 className="w-5 h-5 flex-none" color="#15803D" strokeWidth={2.2} />
          )}
          <div className="text-[13px] font-semibold" style={{ color: anyOffline ? '#B91C1C' : '#15803D' }}>
            {anyOffline ? 'Some services are stopped — affected workers are paused' : 'All services are online and healthy'}
          </div>
        </div>

        {/* Service cards grid */}
        <div
          className="grid gap-[14px]"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))' }}
        >
          {services.map((service) => {
            const st = statusStyle(service.status);
            const online = service.status === 'online';
            const busy = actionLoading[service.name];
            const stats = [
              { k: 'uptime', v: service.uptime || '—' },
              { k: 'memory', v: service.memory_mb > 0 ? `${service.memory_mb}MB` : '—' },
              { k: 'cpu', v: service.cpu !== undefined ? `${service.cpu}%` : '—' },
              { k: 'PID', v: service.pid || '—' },
            ];
            return (
              <div key={service.name} className="sz-fade bg-white border border-[#E6E9EF] rounded-[16px] overflow-hidden">
                <div className="px-[18px] py-[16px]">
                  {/* Header row: dot + name + CRITICAL + status pill */}
                  <div className="flex items-center justify-between gap-[10px]">
                    <div className="flex items-center gap-[9px] min-w-0">
                      <span
                        className="flex-none"
                        style={{ width: 10, height: 10, borderRadius: 999, background: st.dot, boxShadow: `0 0 0 4px ${st.ring}` }}
                      />
                      <span className="font-[Poppins] font-semibold text-[14px] truncate">{service.name}</span>
                      {service.critical && (
                        <span className="flex-none font-[Poppins] font-bold text-[9.5px] text-[#B45309] bg-[#FEF3C7] px-[7px] py-[2px] rounded-[5px]">
                          CRITICAL
                        </span>
                      )}
                    </div>
                    <span
                      className="flex-none font-[Poppins] font-bold text-[10.5px] px-[9px] py-[4px] rounded-full"
                      style={{ color: st.fg, background: st.bg }}
                    >
                      {st.label}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-[12px] text-[#8A90A2] mt-[8px] mb-0 leading-[1.5]">{service.description}</p>

                  {/* 4-up stat tiles (online only) */}
                  {online && (
                    <div className="grid grid-cols-4 gap-[8px] mt-[13px]">
                      {stats.map((s) => (
                        <div key={s.k} className="text-center bg-[#FAFBFC] rounded-[9px] px-[4px] py-[8px]">
                          <div className="font-[Poppins] font-bold text-[13px] truncate">{s.v}</div>
                          <div className="text-[9.5px] text-[#8A90A2] mt-[2px]">{s.k}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Actions row */}
                <div className="flex gap-[7px] px-[18px] pb-[16px]">
                  {online ? (
                    <>
                      <button
                        onClick={() => handleRestart(service.name)}
                        disabled={busy || service.status !== 'online'}
                        className="flex items-center gap-[6px] px-[13px] py-[8px] border border-[#E0E3EB] rounded-[9px] bg-white text-[#2B3142] text-[12px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {busy === 'restart' && <RefreshCw className="w-[13px] h-[13px] animate-spin" />}
                        Restart
                      </button>
                      {service.name !== 'shootzu-backend' && (
                        <button
                          onClick={() => handleStop(service.name)}
                          disabled={busy}
                          className="flex items-center gap-[6px] px-[13px] py-[8px] border border-[#FECACA] rounded-[9px] bg-white text-[#B91C1C] text-[12px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {busy === 'stop' && <RefreshCw className="w-[13px] h-[13px] animate-spin" />}
                          Stop
                        </button>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() => handleStart(service.name)}
                      disabled={busy}
                      className="flex items-center gap-[6px] px-[13px] py-[8px] border border-[#15803D] rounded-[9px] bg-[#15803D] text-white text-[12px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {busy === 'start' && <RefreshCw className="w-[13px] h-[13px] animate-spin" />}
                      Start
                    </button>
                  )}

                  <button
                    onClick={() => toggleLogs(service.name)}
                    className="ml-auto flex items-center gap-[6px] px-[13px] py-[8px] border border-[#E0E3EB] rounded-[9px] bg-white text-[#2B3142] text-[12px] font-semibold cursor-pointer"
                  >
                    {expandedLogs[service.name] ? 'Hide Logs' : 'Logs'}
                  </button>
                </div>

                {/* Dark log viewer */}
                {expandedLogs[service.name] && (
                  <div className="mx-[18px] mb-[16px]">
                    <div className="flex items-center justify-between mb-[6px]">
                      <span className="text-[10.5px] font-semibold text-[#8A90A2] uppercase tracking-[0.4px]">Recent Logs</span>
                      <button
                        onClick={() => fetchLogs(service.name)}
                        disabled={logsLoading[service.name]}
                        className="flex items-center gap-[5px] text-[11px] font-semibold text-[#8A90A2] hover:text-[#2B3142] cursor-pointer disabled:opacity-50"
                      >
                        <RefreshCw className={`w-3 h-3 ${logsLoading[service.name] ? 'animate-spin' : ''}`} />
                        Refresh
                      </button>
                    </div>
                    {logsLoading[service.name] ? (
                      <div className="szmono bg-[#0C1120] rounded-[11px] px-[15px] py-[20px] flex justify-center">
                        <RefreshCw className="w-5 h-5 animate-spin" color="#9FE7DC" />
                      </div>
                    ) : (
                      <pre
                        className="szmono sz-mono bg-[#0C1120] rounded-[11px] px-[15px] py-[13px] text-[11px] leading-[1.7] text-[#9FE7DC] overflow-auto whitespace-pre-wrap"
                        style={{ maxHeight: 160, margin: 0 }}
                      >
                        {serviceLogs[service.name] || 'No logs available'}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default AdminServices;
